// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md

import { PERMISSION_STATUSES } from '@greenlight/shared';
import type { PermissionListItem, PermissionStatus } from '@greenlight/shared';

/** How the grant inventory is grouped into cards. */
export type GroupBy = 'integration' | 'accessHolder';

export type AccessHolderType = 'app' | 'user';

export interface QueueFilters {
  status?: PermissionStatus;
  integrationId?: string;
  accessHolderId?: string;
  accessHolderType?: AccessHolderType;
  /** Pending-only triage filter — keeps the inventory but hides decided grants. */
  pendingOnly?: boolean;
}

export function applyQueueFilters(
  items: readonly PermissionListItem[],
  filters: QueueFilters,
): PermissionListItem[] {
  return items.filter(
    (item) =>
      (filters.status === undefined || item.status === filters.status) &&
      (filters.integrationId === undefined || item.integration_id === filters.integrationId) &&
      (filters.accessHolderId === undefined ||
        (item.principal_id === filters.accessHolderId &&
          (filters.accessHolderType === undefined ||
            item.principal_type === filters.accessHolderType))) &&
      (filters.pendingOnly !== true || item.status === 'pending'),
  );
}

export interface PermissionGroups {
  awaiting: PermissionListItem[];
  recent: PermissionListItem[];
}

export function splitPermissionGroups(items: readonly PermissionListItem[]): PermissionGroups {
  const awaiting: PermissionListItem[] = [];
  const recent: PermissionListItem[] = [];
  for (const item of items) {
    if (item.status === 'pending') {
      awaiting.push(item);
    } else {
      recent.push(item);
    }
  }
  return { awaiting, recent };
}

export type StatusCounts = Record<PermissionStatus, number> & { all: number };

export function statusCounts(items: readonly PermissionListItem[]): StatusCounts {
  const counts = Object.fromEntries(PERMISSION_STATUSES.map((s) => [s, 0])) as Record<
    PermissionStatus,
    number
  >;
  for (const item of items) {
    counts[item.status] += 1;
  }
  return { ...counts, all: items.length };
}

/**
 * Order the actionable queue oldest-first, so a request that has waited longest
 * surfaces at the top. There is no scope-risk tiebreak: a credential's capability
 * is free text IT authors in the external system, so the platform cannot rank a
 * free-text slug by risk (docs/09 § Integration credentials & grants). Pure; returns
 * a new array.
 */
export function sortAwaiting(items: readonly PermissionListItem[]): PermissionListItem[] {
  return [...items].sort((a, b) => a.requested_at.localeCompare(b.requested_at)); // ISO ascending = oldest first
}

/**
 * Order decided grants most-recently-decided first (falls back to request time
 * if a row somehow lacks a decision timestamp). Pure; returns a new array.
 */
export function sortRecent(items: readonly PermissionListItem[]): PermissionListItem[] {
  return [...items].sort((a, b) => {
    const aKey = a.reviewed_at ?? a.requested_at;
    const bKey = b.reviewed_at ?? b.requested_at;
    return bKey.localeCompare(aKey); // ISO descending = most recent first
  });
}

/**
 * Order a single group's grants: pending first (oldest-first), then decided
 * (most-recently-decided first) — the inventory ordering docs/28 § /permissions
 * specifies within each card.
 */
export function orderWithinGroup(items: readonly PermissionListItem[]): PermissionListItem[] {
  const { awaiting, recent } = splitPermissionGroups(items);
  return [...sortAwaiting(awaiting), ...sortRecent(recent)];
}

export interface PermissionGroup {
  /** integration_id or `<principal_type>:<principal_id>` for an access-holder group. */
  id: string;
  /** What entity the card identifies — an integration, an app, or a person. */
  kind: 'integration' | 'app' | 'user';
  /** Display name of the grouping entity. */
  name: string;
  /** Slug of the grouping entity (for tiles / deep-links); null for a person. */
  slug: string | null;
  /** Pending grants in this group. */
  pendingCount: number;
  /** Grants in this group, ordered pending-first then most-recently-decided. */
  items: PermissionListItem[];
}

/**
 * Bucket grants into cards by integration or access holder. The access-holder
 * view keeps app and user grants side by side while preserving their explicit
 * type (ADR-0029 — a personal grant has no app). Within a card grants are
 * ordered pending-first (oldest-first) then decided (recent-first); cards with
 * pending work sort ahead of fully-decided ones, then alphabetically — so triage
 * stays first-class without reducing the page to a bare queue.
 */
export function groupPermissions(
  items: readonly PermissionListItem[],
  by: GroupBy,
): PermissionGroup[] {
  const buckets = new Map<string, PermissionListItem[]>();
  for (const item of items) {
    const key =
      by === 'integration' ? item.integration_id : `${item.principal_type}:${item.principal_id}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  const groups: PermissionGroup[] = Array.from(buckets.entries()).map(([id, members]) => {
    const first = members[0]!;
    const kind =
      by === 'integration'
        ? ('integration' as const)
        : first.principal_type === 'app'
          ? ('app' as const)
          : ('user' as const);
    const name =
      kind === 'integration'
        ? first.integration_name
        : kind === 'app'
          ? (first.app_name ?? 'Unknown app')
          : first.requested_by;
    const slug = kind === 'integration' ? first.integration_slug : first.app_slug;
    return {
      id,
      kind,
      name,
      slug,
      pendingCount: members.filter((m) => m.status === 'pending').length,
      items: orderWithinGroup(members),
    };
  });

  groups.sort((a, b) => {
    const aPending = a.pendingCount > 0 ? 0 : 1;
    const bPending = b.pendingCount > 0 ? 0 : 1;
    if (aPending !== bPending) return aPending - bPending; // groups with pending first
    return a.name.localeCompare(b.name);
  });

  return groups;
}

export function parseQueueParams(params: URLSearchParams): QueueFilters & { group: GroupBy } {
  const raw = params.get('status') || undefined;
  const groupParam = params.get('group');
  const pendingParam = params.get('pending');
  const holderTypeParam = params.get('holderType');
  const legacyAppId = params.get('appId') || undefined;
  const accessHolderType: AccessHolderType | undefined =
    holderTypeParam === 'app' || holderTypeParam === 'user'
      ? holderTypeParam
      : legacyAppId
        ? 'app'
        : undefined;
  return {
    // `group=app&appId=...` is retained as a read-compatible alias for links
    // emitted before the access-holder terminology landed.
    group: groupParam === 'accessHolder' || groupParam === 'app' ? 'accessHolder' : 'integration',
    status: PERMISSION_STATUSES.find((s) => s === raw),
    integrationId: params.get('integrationId') || undefined,
    accessHolderId: params.get('holderId') || legacyAppId,
    accessHolderType,
    pendingOnly: pendingParam === '1' || pendingParam === 'true',
  };
}
